import axios from 'axios';

export interface ApiError {
  message: string;
  fields: Record<string, string>;
  status?: number;
  retryAt?: number;
}

/** Keep server internals out of the UI; only validation messages are displayed. */
export function apiError(failure: unknown, resource = 'Users'): ApiError | null {
  if (axios.isCancel(failure)) return null;
  const error = failure as { code?: string; response?: { status?: number; data?: unknown; headers?: Record<string, unknown> } };
  const status = error?.response?.status;
  const fields: Record<string, string> = {};
  if (status === 400) {
    const data = error.response?.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      for (const [field, value] of Object.entries(data)) {
        if (typeof value === 'string') fields[field] = value;
        else if (Array.isArray(value) && value.every(item => typeof item === 'string')) fields[field] = value.join(' ');
      }
    }
    return { status, fields, message: 'Check the highlighted filters and try again.' };
  }
  if (status === 401) return { status, fields, message: 'Your access could not be verified. Please sign in again.' };
  if (status === 403) return { status, fields, message: `You do not have permission to view ${resource.toLowerCase()}.` };
  if (status === 429) {
    const raw = error.response?.headers?.['retry-after'];
    const value = typeof raw === 'string' || typeof raw === 'number' ? String(raw) : '';
    const retryAt = /^\d+$/.test(value) ? Date.now() + Number(value) * 1000 : Date.parse(value);
    return { status, fields, retryAt: Number.isFinite(retryAt) ? retryAt : undefined, message: 'Too many requests. Please wait before trying again.' };
  }
  return {
    status, fields,
    message: error?.code === 'ECONNABORTED'
      ? `Loading ${resource.toLowerCase()} timed out. Please try again.`
      : status ? `${resource} could not be loaded. Please try again.` : 'Unable to connect. Check your connection and try again.',
  };
}
